import React from 'react';
import { NavLink } from 'react-router-dom';
import { isInternalURL } from '@plone/volto/helpers/Url/Url';
import { toBackendLang } from '@plone/volto/helpers/Utils/Utils';
import config from '@plone/volto/registry';
import './navitem-dropdown.less';

/**
 * Override of Volto's core NavItem.
 *
 * Volto's stock NavItem renders a single link and ignores `item.items`, so the
 * header is always one level deep even when the navigation data is nested
 * (config.settings.navDepth > 1 + plone.navigation_depth on the backend).
 *
 * This version renders the same link, and — when the item has children —
 * wraps it in a hoverable container with a nested <ul> submenu. It recurses,
 * so depth 3+ works (child submenus open to the side).
 *
 * Pure CSS hover: no JS state, no extra dependencies, SSR-safe.
 */

const Link = ({ item, lang, className = 'item' }) => {
  const { settings } = config;
  // The item.url in the root is ''
  if (isInternalURL(item.url) || item.url === '') {
    return (
      <NavLink
        to={item.url === '' ? '/' : item.url}
        className={className}
        activeClassName="active"
        exact={item.url === '' || item.url === `/${toBackendLang(lang)}`}
      >
        {item.title}
      </NavLink>
    );
  }
  return (
    <a
      href={item.url}
      className={className}
      rel="noopener noreferrer"
      target={settings.openExternalLinkInNewTab ? '_blank' : '_self'}
    >
      {item.title}
    </a>
  );
};

const NavItem = ({ item, lang }) => {
  const children = item.items || [];

  if (children.length === 0) {
    return <Link item={item} lang={lang} />;
  }

  return (
    <div className="item has-submenu">
      <Link item={item} lang={lang} className="item submenu-parent" />
      <ul className="submenu">
        {children.map((child) => (
          <li key={child.url || child['@id']} className="submenu-item">
            {/* recurse so third level and deeper also render */}
            <NavItem item={child} lang={lang} />
          </li>
        ))}
      </ul>
    </div>
  );
};

export default NavItem;
